import { Component, HostBinding, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { first } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { PushPipe } from '@ngrx/component';

// Components
import { InputAutocompleteComponent } from '@shared/components/input-autocomplete/input-autocomplete.component';
import { ButtonComponent } from '@shared/components/button/button.component';

// Services
import { DestroyService } from '@services/destroy.service';
import { DetectBrowserTimezoneService } from '@services/detect-browser-timezone.service';

// Store entities
import { getTimezones } from '@store/timezones/timezones.selectors';
import { IState } from '@store/index';
import {
  updateProfileTimezone,
  updateProfileTimezoneSuccess,
  updateProfileTimezoneError,
} from '@store/profile/profile.actions';

// Interfaces
import { ITimezone } from '@interfaces/timezones.interface';
import { IUser } from '@interfaces/user.interface';
import { CUserProfileConfig } from '@modules/profile/constants/profile.constants';

@Component({
  selector: 'app-change-timezone-modal',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    InputAutocompleteComponent,
    ButtonComponent,
    MatIconModule,
    PushPipe,
  ],
  templateUrl: './change-timezone-modal.component.html',
  styleUrl: './change-timezone-modal.component.scss',
})
export class ChangeTimezoneModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ChangeTimezoneModalComponent, IUser | null>);
  private readonly store: Store<IState> = inject(Store);
  private readonly actions$: Actions = inject(Actions);
  private readonly destroy$: DestroyService = inject(DestroyService);
  private readonly detectBrowserTimezoneService = inject(DetectBrowserTimezoneService);
  protected readonly dialogData = inject(MAT_DIALOG_DATA) as {
    timezone?: string | null;
    browserTimezone?: string | null;
  } | null;

  @HostBinding('class') hostClass = 'hmf-change-timezone-modal';

  public timezonesList$: Observable<ITimezone[]> = this.store.select(getTimezones);

  public readonly timezoneCtrl = new FormControl<ITimezone | string | null>(null);

  public readonly displayTimezone = (tz: ITimezone | string | null): string => {
    if (!tz) return '';
    if (typeof tz === 'string') return tz;
    return tz.text || tz.value || '';
  };

  public timezonesList: ITimezone[];

  ngOnInit() {
    if (!this.dialogData?.timezone && !this.dialogData?.browserTimezone) return;

    this.timezonesList$.pipe(takeUntil(this.destroy$)).subscribe(timezones => {
      this.timezonesList = timezones ?? [];
      this.setActiveTimezone();
    });
  }

  public resolveTimezoneText(tz: string | null | undefined): string {
    if (!tz) return '';
    const match = this.timezonesList?.find(item => item.value === tz);
    return match?.text ?? tz.replaceAll('_', ' ');
  }

  public saveTimezone(): void {
    const value = this.timezoneCtrl.value;
    const timezone = typeof value === 'string' ? value : (value?.value ?? null);
    if (!timezone) return;

    this.actions$
      .pipe(
        ofType(updateProfileTimezoneSuccess, updateProfileTimezoneError),
        first(),
        takeUntil(this.destroy$),
      )
      .subscribe(action => {
        if (action.type === updateProfileTimezoneSuccess.type) {
          const browserTz = this.detectBrowserTimezoneService.getBrowserTimezone();

          if (browserTz !== action.data.timezone && this.dialogData) {
            this.dialogData.timezone = action.data.timezone;
            this.dialogData.browserTimezone = browserTz;
            this.setActiveTimezone();
            return;
          }

          this.dialogRef.close();
        }
      });

    this.store.dispatch(updateProfileTimezone(timezone));
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  private setActiveTimezone(): void {
    if (!this.timezonesList || this.timezonesList.length === 0) return;
    const presetTimezone = this.dialogData?.browserTimezone ?? this.dialogData?.timezone;
    const match = this.timezonesList.find(
      tz => tz.value === presetTimezone || tz.text === presetTimezone,
    );
    if (match) {
      this.timezoneCtrl.setValue(match);
    } else {
      this.timezoneCtrl.setValue(presetTimezone ?? null);
    }
  }

  protected readonly configList = CUserProfileConfig;
}
